import "dayjs/locale/de";
import "dayjs/locale/es";
import "dayjs/locale/fr";
import "dayjs/locale/it";
import "dayjs/locale/pt-br";

import dayjs from "dayjs";

import { Language, toLanguage } from "./languages";

const LOCALES: Record<Language, string> = {
  pt: "pt-BR",
  en: "en-US",
  es: "es-ES",
  fr: "fr-FR",
  de: "de-DE",
  it: "it-IT",
};

/**
 * The locale numbers and dates are written in for the editor's language (i18n.language): the browser's own region
 * when it has one ("es-419", "en-GB"), else the language's usual one; English (US) for a language the editor lacks.
 */
export const localeOf = (language?: string | null) => {
  const editorLanguage = toLanguage(language);
  const [base, region] = (language ?? "").replace("_", "-").split("-");
  return region && base.toLowerCase() === editorLanguage
    ? `${editorLanguage}-${region.toUpperCase()}`
    : LOCALES[editorLanguage];
};

type DateInput = string | number | Date | dayjs.Dayjs | null | undefined;

// The date, or nothing when it's missing or broken: Intl throws on an invalid one (the screen would break)
const toDate = (date: DateInput) =>
  date === null || date === undefined || !dayjs(date).isValid() ? undefined : dayjs(date);

/** A day as the language writes it: 16/10/2026, 10/16/2026 (en), 16.10.2026 (de); "" with no date. */
export const formatDate = (date: DateInput, language: string) => {
  const day = toDate(date);
  return day
    ? new Intl.DateTimeFormat(localeOf(language), { day: "2-digit", month: "2-digit", year: "numeric" }).format(
        day.toDate()
      )
    : "";
};

export type DateTimeOptions = {
  /** Read the time in this IANA timezone instead of the device's */
  timeZone?: string;
  /** Write the year when it isn't the current one (both read in `timeZone`): a line from last year says so */
  yearIfNotCurrent?: boolean;
};

const yearIn = (date: Date, timeZone?: string) =>
  new Intl.DateTimeFormat("en-US", { year: "numeric", timeZone }).format(date);

/**
 * A day and time without the year: 16/10, 14:30; 10/16, 2:30 PM (en); "" with no date. In the given timezone when
 * there is one; with the year when asked and it isn't the current one.
 */
export const formatDateTime = (date: DateInput, language: string, options: DateTimeOptions = {}) => {
  const day = toDate(date);
  if (!day) return "";
  const moment = day.toDate();
  const withYear =
    !!options.yearIfNotCurrent && yearIn(moment, options.timeZone) !== yearIn(new Date(), options.timeZone);
  return (
    new Intl.DateTimeFormat(localeOf(language), {
      day: "2-digit",
      month: "2-digit",
      ...(withYear ? { year: "numeric" } : {}),
      hour: "numeric",
      minute: "2-digit",
      timeZone: options.timeZone,
    })
      .format(moment)
      // the narrow space some locales put before AM/PM, as a plain one
      .replace(/\u202f/g, " ")
  );
};

// The month names dayjs writes in each language (English is built in)
const DAYJS_LOCALES: Record<Language, string> = { pt: "pt-br", en: "en", es: "es", fr: "fr", de: "de", it: "it" };

/** A month as titles show it: Outubro 2026, October 2026, Oktober 2026; "" with no date. */
export const formatMonthYear = (date: DateInput, language: string) => {
  const day = toDate(date);
  if (!day) {
    return "";
  }
  const text = day.locale(DAYJS_LOCALES[toLanguage(language)]).format("MMMM YYYY");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/** The decimal separator of the language: "," (pt, es, fr, de, it) or "." (en). */
export const decimalSeparator = (language: string) =>
  new Intl.NumberFormat(localeOf(language)).formatToParts(1.5).find(({ type }) => type === "decimal")?.value ?? ".";
